import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Pending finance events — the auto-generated drafts the cron creates
// each day. The user reviews them in <PendingReviewSheet />, edits
// amounts if reality differed, then confirms.
//
// Confirmed-vs-pending matters for the badge count: we only want to
// nag the user when there's actually something to review.

export function usePendingFinanceEvents() {
  const { user } = useAuth()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('pending_finance_events')
      .select('*')
      .eq('user_id', user.id)
      .eq('status', 'pending')
      .order('scheduled_date', { ascending: false })
    if (err) setError(err.message)
    else setEvents(data || [])
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  // Bulk-confirm. p_overrides is { [eventId]: amount } for any rows
  // the user edited. The RPC returns the new balance so the UI can
  // toast it.
  const confirm = useCallback(async (eventIds, overrides = {}) => {
    if (!user || !eventIds.length) return null
    const { data, error: err } = await supabase.rpc('confirm_pending_events', {
      p_event_ids: eventIds,
      p_overrides: overrides,
    })
    if (err) { setError(err.message); return null }
    await fetch()
    const row = Array.isArray(data) ? data[0] : data
    return row || null
  }, [user, fetch])

  // Skip an event — won't move the balance, won't decrement
  // remaining_months. The row stays in history for audit.
  const skip = useCallback(async (eventId) => {
    if (!user) return false
    const { error: err } = await supabase
      .from('pending_finance_events')
      .update({ status: 'skipped' })
      .eq('id', eventId)
      .eq('user_id', user.id)
    if (err) { setError(err.message); return false }
    await fetch()
    return true
  }, [user, fetch])

  return {
    events,
    loading,
    error,
    confirm,
    skip,
    refetch: fetch,
    count: events.length,
  }
}

// "Dues this month" — synthesised on the fly via the list_dues_this_month
// RPC. Each row is one recurring item with a status flag (unsettled /
// pending / confirmed) and the event_id if one already exists. Used by
// <DuesSheet />.
export function useDuesThisMonth() {
  const { user } = useAuth()
  const [dues, setDues] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase.rpc('list_dues_this_month')
    if (err) setError(err.message)
    else setDues(data || [])
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  // Materialise pending events for a chosen subset (or all unsettled
  // if sourceIds is undefined), then immediately confirm them in the
  // same call. Used by the "pay all" / "pay selected" buttons.
  const payNow = useCallback(async (sourceIds, overrides = {}) => {
    if (!user) return null
    // Step 1: create events for the items.
    const { data: created, error: createErr } = await supabase.rpc('create_dues_events_now', {
      p_source_ids: sourceIds && sourceIds.length > 0 ? sourceIds : null,
    })
    if (createErr) { setError(createErr.message); return null }
    if (!created || created.length === 0) {
      await fetch()
      return { new_balance: null, event_count: 0 }
    }
    const eventIds = created.map((e) => e.id)
    // Step 2: confirm them (transactional inside the RPC).
    const { data: result, error: confirmErr } = await supabase.rpc('confirm_pending_events', {
      p_event_ids: eventIds,
      p_overrides: overrides,
    })
    if (confirmErr) { setError(confirmErr.message); return null }
    await fetch()
    const row = Array.isArray(result) ? result[0] : result
    return row || { new_balance: null, event_count: eventIds.length }
  }, [user, fetch])

  const unsettledTotal = dues
    .filter((d) => d.status === 'unsettled')
    .reduce((s, d) => s + Number(d.amount), 0)

  const unsettledCount = dues.filter((d) => d.status === 'unsettled').length

  return {
    dues,
    loading,
    error,
    refetch: fetch,
    payNow,
    unsettledTotal,
    unsettledCount,
  }
}

// Every confirmed event the user owns, keyed by `${kind}:${id}:${period_month}`.
// Consumed by the cashflow forecast and the Upcoming30Days widget to
// skip items the user has already settled for a given future cycle.
export function useConfirmedFinanceEvents() {
  const { user } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    const { data } = await supabase
      .from('pending_finance_events')
      .select('source_kind, source_id, period_month')
      .eq('user_id', user.id)
      .eq('status', 'confirmed')
    setRows(data || [])
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  // Refetch when the dues sheet finishes confirming.
  useEffect(() => {
    const onRefresh = () => fetch()
    window.addEventListener('phoenix:refresh-finance', onRefresh)
    return () => window.removeEventListener('phoenix:refresh-finance', onRefresh)
  }, [fetch])

  const paidSet = useMemo(() => {
    const set = new Set()
    for (const r of rows) {
      set.add(`${r.source_kind}:${r.source_id}:${r.period_month}`)
    }
    return set
  }, [rows])

  return { paidSet, loading, refetch: fetch }
}
